import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import AttToken from '../../services/AttToken';
import {
  consultarConfigUnica,
  consultarModeloAgendamento,
  consultarModeloCadastro,
  FluxoConfigError,
  ModeloCadastroApi,
  RegraCadastroApi,
  salvarConfig,
} from '../../services/FluxoConfigs';
import { FluxoConfig, MapeamentoCadastro } from '../../types/FluxoConfig';
import { empresaType } from '../../context/EmpresasContext';

type Props = {
  empresa: empresaType;
  open: boolean;
  onClose: () => void;
};

const blank = (): FluxoConfig => ({
  idFluxo: '',
  idChecklist: '',
  idModeloAgendamento: '',
  adaptador: 'generico',
  ativo: true,
  estrategiaIdAgendamento: { tipo: 'campo', campo: '' },
  mapeamento: { cadastros: [], camposAgendamento: {} },
});

function regraId(regra: RegraCadastroApi) {
  return regra.idRegraCadastro || regra.id || '';
}

function modeloCadastroId(regra: RegraCadastroApi) {
  return regra.idModeloCadastro || regra.modeloCadastroId || '';
}

function nomesCampos(modelo?: ModeloCadastroApi) {
  if (!modelo?.campos) return [];
  if (Array.isArray(modelo.campos)) {
    return modelo.campos.map(campo => campo.nome || campo.id || '').filter(Boolean);
  }
  return Object.keys(modelo.campos);
}

type CadastroCardProps = {
  empresaId: string;
  mapeamento: MapeamentoCadastro;
  onChange: (campos: Record<string, string>) => void;
};

function CadastroMapCard({ empresaId, mapeamento, onChange }: CadastroCardProps) {
  const modeloQuery = useQuery(
    ['cadastro-modelo', empresaId, mapeamento.idModeloCadastro],
    () => consultarModeloCadastro(empresaId, mapeamento.idModeloCadastro),
    { enabled: !!mapeamento.idModeloCadastro, retry: false }
  );
  const campos = nomesCampos(modeloQuery.data);

  return (
    <Box border={1} borderColor="divider" borderRadius={1} padding={2}>
      <Typography variant="subtitle1">{mapeamento.nome}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Modelo de Cadastro: {mapeamento.idModeloCadastro}
      </Typography>
      {modeloQuery.isLoading && <Typography>Carregando campos do cadastro…</Typography>}
      {modeloQuery.isError && (
        <Alert severity="error">Não foi possível carregar os campos deste Modelo de Cadastro.</Alert>
      )}
      <Stack spacing={2}>
        {campos.map(campo => (
          <TextField
            key={campo}
            required
            label={`Campo de origem para ${campo}`}
            helperText="Caminho relativo ao objeto agendamento. Ex.: motorista.cpf"
            value={mapeamento.campos[campo] || ''}
            onChange={event => onChange({
              ...mapeamento.campos,
              [campo]: event.target.value,
            })}
            fullWidth
          />
        ))}
      </Stack>
    </Box>
  );
}

export default function ModalWebhookAgendamento({ empresa, open, onClose }: Props) {
  const [config, setConfig] = useState<FluxoConfig>(blank());
  const [modeloAgendamentoConsultado, setModeloAgendamentoConsultado] = useState('');
  const [tipoEstrategia, setTipoEstrategia] = useState<'campo' | 'template'>('campo');
  const [campoUnico, setCampoUnico] = useState('');
  const [camposMultiplos, setCamposMultiplos] = useState<string[]>(['chave']);
  const [loaded, setLoaded] = useState(false);
  const client = useQueryClient();
  const empresaId = empresa.id || '';
  const authQuery = useQuery(
    ['fluxo-config-auth', empresaId],
    AttToken,
    {
      enabled: open,
    }
  );
  const authorized = true;
  const configQuery = useQuery(
    ['fluxo-config', authQuery.data?.username, empresaId],
    () => consultarConfigUnica(empresaId),
    { enabled: open && !!authorized, retry: false }
  );
  const agendamentoModeloQuery = useQuery(
    ['agendamento-modelo', empresaId, modeloAgendamentoConsultado],
    () => consultarModeloAgendamento(empresaId, modeloAgendamentoConsultado),
    {
      enabled: open && authorized && !!modeloAgendamentoConsultado,
      retry: false,
    }
  );

  useEffect(() => {
    setConfig(blank());
    setModeloAgendamentoConsultado('');
    setTipoEstrategia('campo');
    setCampoUnico('');
    setCamposMultiplos(['chave']);
    setLoaded(false);
  }, [empresaId, open]);

  useEffect(() => {
    if (configQuery.data && !loaded) {
      setConfig(configQuery.data);
      setModeloAgendamentoConsultado(configQuery.data.idModeloAgendamento);
      if (configQuery.data.estrategiaIdAgendamento.tipo === 'campo') {
        setTipoEstrategia('campo');
        setCampoUnico(configQuery.data.estrategiaIdAgendamento.campo);
      } else {
        setTipoEstrategia('template');
        setCamposMultiplos(configQuery.data.estrategiaIdAgendamento.campos);
      }
      setLoaded(true);
    }
  }, [configQuery.data, loaded]);

  useEffect(() => {
    const regras = agendamentoModeloQuery.data?.regrasCadastro;
    if (!regras) return;

    setConfig(current => ({
      ...current,
      mapeamento: {
        ...current.mapeamento,
        cadastros: regras.map(regra => {
          const idRegraCadastro = regraId(regra);
          const idModeloCadastro = modeloCadastroId(regra);
          const anterior = current.mapeamento.cadastros.find(item => (
            item.idRegraCadastro === idRegraCadastro
          ));

          return {
            idRegraCadastro,
            idModeloCadastro,
            nome: regra.nome || anterior?.nome || 'Cadastro recebido',
            campos: anterior?.campos || {},
          };
        }),
      },
    }));
  }, [agendamentoModeloQuery.data]);

  const webhookUrl = useMemo(() => {
    const base = process.env.REACT_APP_EMPRESA_API_BASE_URL?.replace(/\/empresa\/?$/, '') || '';
    return config.idFluxo
      ? `${base}/empresa/public/agendamento/${encodeURIComponent(config.adaptador)}/${encodeURIComponent(config.idFluxo)}`
      : '';
  }, [config.adaptador, config.idFluxo]);

  const save = useMutation(() => salvarConfig(empresaId, config), {
    onSuccess: () => {
      client.invalidateQueries(['fluxo-config']);
      toast.success('Configuração salva.');
      setLoaded(true);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Não foi possível salvar a configuração.');
    },
  });

  const update = (field: keyof FluxoConfig, value: any) => {
    setConfig(current => ({ ...current, [field]: value }));
  };

  const canSave = !!config.idFluxo
    && !!config.idChecklist
    && !!config.idModeloAgendamento
    && !!config.adaptador
    && modeloAgendamentoConsultado === config.idModeloAgendamento
    && (tipoEstrategia === 'campo'
      ? !!campoUnico
      : camposMultiplos.length > 0 && camposMultiplos.every(Boolean))
    && agendamentoModeloQuery.isSuccess
    && config.mapeamento.cadastros.every(cadastro => (
      !!cadastro.idRegraCadastro
      && !!cadastro.idModeloCadastro
      && Object.values(cadastro.campos).every(Boolean)
    ));

  const updateCadastroCampos = (idRegraCadastro: string, campos: Record<string, string>) => {
    setConfig(current => ({
      ...current,
      mapeamento: {
        ...current.mapeamento,
        cadastros: current.mapeamento.cadastros.map(cadastro => (
          cadastro.idRegraCadastro === idRegraCadastro ? { ...cadastro, campos } : cadastro
        )),
      },
    }));
  };

  const updateCamposAgendamento = (campo: 'idUnidade' | 'dataPrevista', valor: string) => {
    setConfig(current => ({
      ...current,
      mapeamento: {
        ...current.mapeamento,
        camposAgendamento: {
          ...current.mapeamento.camposAgendamento,
          [campo]: valor,
        },
      },
    }));
  };

  const selecionarEstrategia = (tipo: 'campo' | 'template') => {
    setTipoEstrategia(tipo);
    update(
      'estrategiaIdAgendamento',
      tipo === 'campo'
        ? { tipo: 'campo', campo: campoUnico }
        : { tipo: 'template', campos: camposMultiplos }
    );
  };

  const atualizarCampoUnico = (campo: string) => {
    setCampoUnico(campo);
    if (tipoEstrategia === 'campo') {
      update('estrategiaIdAgendamento', { tipo: 'campo', campo });
    }
  };

  const adicionarCampoMultiplo = () => {
    const novosCampos = [...camposMultiplos, 'chave'];
    setCamposMultiplos(novosCampos);
    if (tipoEstrategia === 'template') {
      update('estrategiaIdAgendamento', { tipo: 'template', campos: novosCampos });
    }
  };

  const atualizarCampoMultiplo = (indice: number, valor: string) => {
    const novosCampos = camposMultiplos.map((campo, campoIndice) => (
      campoIndice === indice ? valor : campo
    ));
    setCamposMultiplos(novosCampos);
    if (tipoEstrategia === 'template') {
      update('estrategiaIdAgendamento', { tipo: 'template', campos: novosCampos });
    }
  };
  const existingError = configQuery.error as FluxoConfigError | null;

  return (
    <Dialog
      open={open}
      onClose={save.isLoading ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Webhook de agendamento — {empresa.nome} ({empresaId})
      </DialogTitle>
      <DialogContent dividers>
        {!authorized && !authQuery.isLoading && (
          <Alert severity="warning">
            A API atual só permite configurar a empresa da sessão. O contrato
            administrativo multiempresa ainda não foi publicado.
          </Alert>
        )}
        {authorized && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              A API atual consulta apenas uma configuração ativa. Edição por
              Fluxo, reativação e listagem completa dependem dos novos endpoints
              autorizados.
            </Alert>
            {existingError && existingError.status !== 404 && (
              <Alert severity="error">{existingError.message}</Alert>
            )}
            <Typography variant="h6">Operação</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                required
                label="ID do Fluxo"
                value={config.idFluxo}
                onChange={e => update('idFluxo', e.target.value)}
                fullWidth
              />
              <TextField
                required
                label="Checklist inicial"
                value={config.idChecklist}
                onChange={e => update('idChecklist', e.target.value)}
                fullWidth
              />
              <TextField
                required
                label="Modelo de Agendamento"
                value={config.idModeloAgendamento}
                onChange={e => update('idModeloAgendamento', e.target.value)}
                onBlur={event => setModeloAgendamentoConsultado(event.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label="Versão do Fluxo (opcional)"
              type="number"
              value={config.versaoFluxo || ''}
              onChange={e => update('versaoFluxo', e.target.value ? Number(e.target.value) : undefined)}
              inputProps={{ min: 1 }}
            />
            {agendamentoModeloQuery.isLoading && (
              <Typography>Carregando regras de cadastro…</Typography>
            )}
            {agendamentoModeloQuery.isError && (
              <Alert severity="error">
                Não foi possível carregar as regras do Modelo de Agendamento.
              </Alert>
            )}
            {config.idModeloAgendamento && agendamentoModeloQuery.isSuccess && (
              <>
                <Typography variant="h6">Cadastros</Typography>
                {config.mapeamento.cadastros.length === 0 ? (
                  <Alert severity="info">
                    Este Modelo de Agendamento não retornou regras de cadastro.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {config.mapeamento.cadastros.map(cadastro => (
                      <CadastroMapCard
                        key={cadastro.idRegraCadastro}
                        empresaId={empresaId}
                        mapeamento={cadastro}
                        onChange={campos => updateCadastroCampos(cadastro.idRegraCadastro, campos)}
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}
            <Typography variant="h6">Recebimento</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                required
                label="Adaptador"
                helperText="Use somente adaptadores cujo contrato esteja aprovado."
                value={config.adaptador}
                onChange={e => update('adaptador', e.target.value)}
                fullWidth
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={config.ativo}
                    onChange={e => update('ativo', e.target.checked)}
                  />
                )}
                label="Configuração ativa"
              />
            </Stack>
            <Box>
              <Typography variant="subtitle1">Estratégia do código externo</Typography>
              <RadioGroup
                row
                value={tipoEstrategia}
                onChange={event => selecionarEstrategia(event.target.value as 'campo' | 'template')}
              >
                <FormControlLabel
                  value="campo"
                  control={<Radio />}
                  label="Campo Único"
                />
                <FormControlLabel
                  value="template"
                  control={<Radio />}
                  label="Campos Múltiplos"
                />
              </RadioGroup>
            </Box>
            {tipoEstrategia === 'campo' ? (
              <TextField
                required
                label="Campo do código externo"
                helperText="Caminho relativo ao objeto agendamento. Ex.: codigo"
                value={campoUnico}
                onChange={event => atualizarCampoUnico(event.target.value)}
                fullWidth
              />
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Button variant="outlined" onClick={adicionarCampoMultiplo}>
                    + Adicionar campo
                  </Button>
                </Box>
                {camposMultiplos.map((campo, indice) => (
                  <TextField
                    key={indice}
                    required
                    label={`Campo ${indice + 1}`}
                    helperText='Chave relativo ao objeto agendamento. Exs.: "cpfMotorista", "rgPaciente", "placaCavalo"'
                    value={campo}
                    onChange={event => atualizarCampoMultiplo(indice, event.target.value)}
                    fullWidth
                  />
                ))}
              </Stack>
            )}
            <Alert severity="info">
              Os cartões acima são criados pelas regras do Modelo de Agendamento.
              O ID do Modelo de Cadastro é definido pela regra e não pode ser
              alterado aqui.
            </Alert>
            <Typography variant="h6">Agendamento</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Caminho do ID da unidade"
                helperText="Ex.: unidadeId. O valor recebido deve ser um ID real de unidade."
                value={config.mapeamento.camposAgendamento?.idUnidade || ''}
                onChange={event => updateCamposAgendamento('idUnidade', event.target.value)}
                fullWidth
              />
              <TextField
                label="Caminho da data prevista"
                helperText="Ex.: previsao. Prefira data ISO 8601 com fuso."
                value={config.mapeamento.camposAgendamento?.dataPrevista || ''}
                onChange={event => updateCamposAgendamento('dataPrevista', event.target.value)}
                fullWidth
              />
            </Stack>
            <Typography variant="h6">URL de criação</Typography>
            <Box display="flex" gap={1}>
              <TextField value={webhookUrl} fullWidth InputProps={{ readOnly: true }} />
              <Button
                disabled={!webhookUrl}
                onClick={() => navigator.clipboard?.writeText(webhookUrl)}
                startIcon={<ContentCopyIcon />}
              >
                Copiar
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isLoading}>
          Fechar
        </Button>
        <Button
          variant="contained"
          disabled={!authorized || !canSave || save.isLoading}
          onClick={() => {
            if (loaded && !window.confirm('Esta operação substitui a configuração inteira. Deseja continuar?')) return;
            save.mutate();
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
